// frontend/src/components/Recipe/RecipeDetail.jsx (또는 pages 경로)
// -------------------------------------------------------------
// 🍸 RecipeDetail 컴포넌트
// - URL의 id 파라미터를 기반으로 특정 칵테일 상세 정보를 불러옴
// - 로딩 / 에러 / 데이터 표시 3가지 상태를 처리
// - 좌측에는 레시피 설명, 우측에는 이미지 및 코멘트를 표시
// - 목록으로 돌아갈 때, 이전에 보고 있던 정렬/쿼리 상태 유지
// -------------------------------------------------------------

import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import CocktailLikeButton from "@/components/Like/CocktailLikeButton";
import api from "@/lib/api";

export default function RecipeDetail() {
  // --- URL 파라미터(id) 추출 ---
  const { id } = useParams();

  // --- 네비게이션/위치 ---
  const navigate = useNavigate();
  const location = useLocation(); // RecipeList에서 넘긴 state 접근용

  // --- 상태 관리 ---
  const [cocktail, setCocktail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // --- 데이터 불러오기 ---
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/api/cocktails/${id}`);
        setCocktail(res.data || null);
      } catch (err) {
        console.error(err);
        setError("칵테일 레시피를 불러오는 도중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // --- 목록으로 돌아가기 (정렬 상태 유지) ---
  const handleBack = () => {
    const fallback = "/cocktails";
    const from = location.state?.from || fallback;
    navigate(from);
  };

  // --- 상태별 렌더링 처리 ---
  if (loading) {
    return (
      <article className="max-w-4xl mx-auto mt-12 rounded-2xl p-12 bg-white/5 border border-white/10 text-white shadow-[0_6px_20px_rgba(0,0,0,.35)] animate-pulse">
        불러오는 중...
      </article>
    );
  }

  if (error) return <div className="text-red-400">{error}</div>;

  if (!cocktail) return <div className="text-white">레시피가 없습니다</div>;

  // --- 메인 콘텐츠 렌더링 ---
  return (
    <article
      className="text-white max-w-4xl mx-auto flex flex-col md:flex-row gap-8
                 border border-white/10 bg-white/5 rounded-2xl p-8 md:p-12 mt-12
                 shadow-[0_6px_20px_rgba(0,0,0,.35)] hover:shadow-[0_12px_28px_rgba(0,0,0,.45)]
                 transition-shadow duration-300 backdrop-blur-[2px]"
    >
      {/* ---------------- 좌측 정보 영역 ---------------- */}
      <div className="flex-1 mr-0 md:mr-8">
        {/* 목록으로 돌아가기 버튼 (정렬 상태 유지) */}
        <button
          onClick={handleBack}
          className="text-sm text-white/70 hover:font-bold hover:cursor-pointer"
        >
          ← 목록으로
        </button>

        {/* 칵테일 제목 및 도수 */}
        <h1 className="text-3xl font-extrabold mt-3 mb-2 tracking-tight">
          {cocktail.name}
        </h1>
        <p className="text-white/70 mb-6">
          도수: ~{cocktail.abv ?? cocktail.alcohol ?? 0}%
        </p>

        {/* --- 태그 섹션 --- */}
        {Array.isArray(cocktail.tags) && cocktail.tags.length > 0 && (
          <section className="mb-6">
            <h2 className="text-lg font-semibold mb-2">태그</h2>
            <ul className="flex gap-2 flex-wrap">
              {cocktail.tags.map((tag) => (
                <li
                  key={tag}
                  className="px-2 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/40 text-xs text-cyan-200 hover:scale-105
                             transition-transform hover:cursor-pointer"
                  onClick={() => navigate(`/search?keyword=${tag}`)}
                >
                  #{tag}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* 구분선 */}
        <div className="my-6 h-px w-full bg-white/20" />

        {/* --- 재료 섹션 --- */}
        {Array.isArray(cocktail.ingredients) &&
          cocktail.ingredients.length > 0 && (
            <section className="mb-6">
              <h2 className="text-lg font-semibold mb-2">재료</h2>
              <ul className="pl-5 space-y-1 marker:text-white/60 list-disc">
                {cocktail.ingredients.map((ing, i) => (
                  <li key={i} className="text-white/90">
                    {ing.name} — {ing.amount}
                  </li>
                ))}
              </ul>
            </section>
          )}

        {/* 구분선 */}
        <div className="my-6 h-px w-full bg-white/20" />

        {/* --- 만드는 법 섹션 --- */}
        {Array.isArray(cocktail.steps) && cocktail.steps.length > 0 && (
          <section>
            <h2 className="text-lg font-semibold mb-2">만드는 법</h2>
            <ol className="pl-5 space-y-2 list-decimal marker:text-white/60">
              {cocktail.steps.map((step, i) => (
                <li key={i} className="text-white/90 leading-relaxed">
                  {step}
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>

      {/* ---------------- 우측 사이드 영역 ---------------- */}
      <aside className="w-full md:w-64 shrink-0">
        {/* 이미지 카드 */}
        <div
          className="rounded-xl overflow-hidden border border-white/10 bg-black/20
                     shadow-[0_8px_24px_rgba(0,0,0,.45)]"
        >
          <img
            src={cocktail.image}
            alt={cocktail.name}
            className="object-cover w-full h-64 md:h-[340px]"
          />
        </div>

        {/* 코멘트 (선택적 표시) */}
        {cocktail.comment && (
          <p className="text-center mt-4 text-gray-300 px-3 py-2 bg-white/10 rounded-xl text-sm border border-white/10">
            {cocktail.comment}
          </p>
        )}

        {/* 좋아요 버튼 (오른쪽 아래) */}
        <div className="flex justify-end mt-8">
          <CocktailLikeButton cocktailId={id} />
        </div>
      </aside>
    </article>
  );
}
